/**
 * `MyCrate` — the favorites screen. `docs/design-direction.md` §8.5, `spec.md` §6.6.
 *
 * 45s standing in a box, seen from above and in front. Two columns of records dropped
 * into card-stock sleeves, alternating ±0.8°, deliberately uneven box depths, a
 * stencilled `MY CRATE` masthead with a mono count and runtime, and the
 * `A–Z / RECENT / RANDOM` switch as three stamped ring chips.
 *
 * ## The save affordance is a bracket pair, and it is never a heart
 *
 * §8.5 and §12.21 are unambiguous: the "save" affordance is a 1px-stroked `[ ]` that
 * fills solid `--accent` when the record is filed. {@link FileBracket} is the only
 * implementation of it in the app, its accessible name is a Russian verb phrase, and
 * its state is carried by `aria-pressed` rather than by a changing label — a toggle
 * whose name changes under the user is one a screen reader announces twice and a user
 * cannot find again.
 *
 * ## Optimism with a real rollback path
 *
 * Every mutation this screen can start applies locally on the tap and reconciles
 * afterwards. `store/library`'s `toggleFavorite` already owns the favourite half of
 * that contract **including the rollback**: it restores the previous map and records
 * the failure, so a flaky connection cannot leave a record silently un-filed. This
 * screen does not duplicate that and does not second-guess it — it renders from
 * `selectIsFavorite`, the store's single sanctioned read, so a record filed here
 * lights up everywhere else in the app without walking one cached page.
 *
 * ## Two honesty rules the masthead obeys
 *
 * The count is printed from `total`, which is a real `COUNT(*)` (§6.6). The **runtime
 * is printed only once the whole crate is loaded** — `nextCursor === null` — because
 * the sum of one page's durations is not the crate's runtime, and §12.6 bans a
 * fabricated number as firmly as it bans a synthesised waveform. A partially paged
 * crate shows a count and no runtime, which is the truth.
 *
 * ## Tap plays, long press flips, and the strip is still reachable by keyboard
 *
 * A tap slides the record 12px up out of its sleeve and starts it (§8.5). The lift is
 * a **resting** style of the playing record rather than a one-shot animation, so the
 * record that is playing stays out of its sleeve and the crate reads at a glance — and
 * the play call is issued in the same handler with no timer in front of it, because a
 * `setTimeout` before `play()` would put the call outside the user gesture and lose
 * the autoplay unlock that `engine/unlock.ts` exists to win.
 *
 * A 500 ms press flips the sleeve to a mono spec back and reveals the 1px-ruled action
 * strip. **A long press is a shortcut, never the only path**: a keyboard has no long
 * press, so the same strip is opened by a 44px disclosure control carrying §9.3's
 * Russian name. The strip prints its own labels in English because it is a silkscreen
 * control surface, which §11 licenses by name, and every button on it carries a
 * Russian accessible name — a destructive action must be unambiguous to a screen
 * reader.
 *
 * ## What the spec back can honestly print
 *
 * §8.5 asks for "added date, plays, riddim". `GET /api/favorites` returns
 * `Page<TrackSummary>` (§6.6) and a `TrackSummary` carries none of those three, so the
 * back prints what the row actually holds — side, year, BPM, catalogue number, length
 * — through `SpecTable`, which omits a missing value rather than printing a dash
 * (§12.6). Inventing an "added" date because the design named one would be the same
 * sin as inventing a waveform.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { CSSProperties, JSX } from 'react';

import { plateCssVars, plateFromSeed } from '@bl/shared';
import type { LibrarySort, Plate, PlaylistHeader, TrackSummary } from '@bl/shared';

import type { ApiClient, ApiFailure, FavoritesArgs } from '../lib/api';
import { api, toApiFailure } from '../lib/api';
import type { TelegramFacade } from '../lib/telegram';
import { telegram as defaultTelegram } from '../lib/telegram';
import type { ScreenProps } from '../app/router';
import { buildPath, navigate } from '../app/router';
import type { Wiring } from '../app/wiring';
import { getWiring } from '../app/wiring';
import { Empty } from '../app/states/Empty';
import { ErrorState } from '../app/states/ErrorState';
import { Loading, StateFrame } from '../app/states/Loading';
import { MORE_LABEL } from '../components/TrackRow';
import { formatTimecode } from '../components/Scrub';
import { DownbeatRule } from '../components/primitives/DownbeatRule';
import { Halftone } from '../components/primitives/Halftone';
import { cx } from '../components/primitives/Rule';
import { FILE_LABEL_RU, FileBracket } from '../components/FileBracket';
import { SpecTable } from '../components/primitives/SpecTable';
import { TagChip } from '../components/primitives/TagChip';
import { selectIsFavorite, useLibraryStore } from '../store/library';
import { usePlayerStore } from '../store/player';
import { useQueueStore } from '../store/queue';

import styles from './crateshelf.module.css';

/*
 * Re-exported, not re-declared: `components/FileBracket` is now the single definition,
 * because a record has to be fileable from the crate too — the shelf was the only place
 * that could file one, so "Моё" could be filled only from itself. Every existing import
 * of these two names keeps resolving here.
 */
export { FILE_LABEL_RU, FileBracket };
export type { FileBracketProps } from '../components/FileBracket';

/* ══ copy ══════════════════════════════════════════════════════════════════════════ */

/** Label print. Latin, and caps are licensed for Latin (§4.3). */
export const MY_CRATE_TITLE = 'MY CRATE';

/** The three action-strip controls: English print, Russian accessible names (§11). */
export const CRATE_ACTIONS = Object.freeze({
  next: Object.freeze({ print: 'PLAY NEXT', ru: 'Поставить следующим' }),
  share: Object.freeze({ print: 'SHARE TO CHAT', ru: 'Отправить в чат' }),
  remove: Object.freeze({ print: 'REMOVE', ru: 'Убрать из ящика' }),
});

export const SORT_SWITCH_LABEL_RU = 'Порядок в ящике';
export const PLAYLISTS_HEADING_RU = 'Подборки';
export const SHELF_LABEL_RU = 'Мой ящик';

/**
 * The way into `/history` (F12). It is the ONLY one: the nav strip has exactly three
 * tabs and `main.tsx` already files `/history` under «МОЁ», so the highlight resolves
 * itself — the route simply had no link anywhere in the app and could not be reached
 * by a finger at all.
 */
export const HISTORY_LINK_RU = 'Недавно слушали';

/**
 * The way in to ОФОРМЛЕНИЕ (`/skins`).
 *
 * It stands here, beside `Недавно слушали`, because МОЁ is the only tab root that is
 * about the person rather than about the catalogue — and because this repository's
 * dominant defect class is a feature that is built, tested and has no entry point
 * (`wiki/pages/unreached-features-2026-09-05.md`). A picker nobody can reach is that
 * bug, not a feature.
 */
export const SKINS_LINK_RU = 'Оформление';

/* ══ the sort switch ═══════════════════════════════════════════════════════════════ */

export interface CrateSortOption {
  readonly sort: LibrarySort;
  /** Latin print (§4.3). */
  readonly print: string;
}

/** `A–Z / RECENT / RANDOM` (§8.5), in the order they are printed. */
export const CRATE_SORTS: readonly CrateSortOption[] = Object.freeze([
  Object.freeze({ sort: 'alpha' as const, print: 'A–Z' }),
  Object.freeze({ sort: 'recent' as const, print: 'RECENT' }),
  Object.freeze({ sort: 'random' as const, print: 'RANDOM' }),
]);

/** `SeedParam` is `0 … 4294967295` (`schemas/common.ts`). */
const SEED_SPACE = 4294967296;

/**
 * A fresh 32-bit seed for `sort=random`.
 *
 * `crypto.getRandomValues` where it exists, `Math.random` otherwise. The seed's only
 * job is to keep one random listing stable **across its own pages** — §6.6 requires it
 * for `sort=random` because a `RANDOM()` listing paged without one repeats rows and
 * drops others — so cryptographic quality is irrelevant and availability is not.
 */
export function newSeed(): number {
  const c: Crypto | undefined = globalThis.crypto;
  if (c !== undefined && typeof c.getRandomValues === 'function') {
    const out = new Uint32Array(1);
    c.getRandomValues(out);
    return out[0] ?? 0;
  }
  return Math.floor(Math.random() * SEED_SPACE);
}

/* ══ formatting ════════════════════════════════════════════════════════════════════ */

/**
 * `07:41:22` — a crate runtime: hours always printed, always two digits.
 *
 * Deliberately **not** `components/Scrub`'s `formatTimecode`, which is a *playhead*
 * format: it drops the hour field below an hour and never pads it, because a timecode
 * that grows a field mid-track makes the transport jump. A masthead runtime is a
 * fixed-width column figure (§8.2 prints `128 ЗАПИСЕЙ · 07:41:22`), so it is a
 * different format rather than a variant of the same one — and a track's own length,
 * in the spec table below, still uses `formatTimecode`.
 */
export function formatRuntime(seconds: number): string {
  const total = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  return [hours, minutes, secs].map((part) => String(part).padStart(2, '0')).join(':');
}

/**
 * The masthead's mono line: the count, and the runtime **only when it is real**.
 *
 * `total` is a `COUNT(*)`; the runtime can only be summed from rows we hold, so it is
 * printed exclusively once `nextCursor === null` says we hold all of them. Anything
 * else is omitted rather than estimated (§12.6). With neither fact available the line
 * itself is absent — never an empty rule hanging in the air.
 */
export function crateMetaLine(
  total: number | null,
  items: readonly TrackSummary[],
  complete: boolean,
): string | null {
  const parts: string[] = [];
  if (total !== null) parts.push(`${String(total)} записей`);
  if (complete && items.length > 0) {
    parts.push(formatRuntime(items.reduce((sum, item) => sum + item.durationS, 0)));
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * The uneven sleeve depth, derived from the track's own plate seed.
 *
 * A real shelf is not a grid of identical tiles (§8.5) — but "uneven" must not mean
 * "random": the same record has to sit at the same depth on every device and after
 * every reload, exactly like its plate. Four steps, every one a spacing token, so
 * nothing off §5.1's scale reaches the stylesheet.
 */
const SLEEVE_LIFTS = ['0px', 'var(--s1)', 'var(--s2)', 'var(--s3)'] as const;

export function sleeveLift(seed: number): string {
  return SLEEVE_LIFTS[(seed >>> 0) % SLEEVE_LIFTS.length] ?? '0px';
}

/* ══ the filing bracket ════════════════════════════════════════════════════════════ */

/* ══ the long press ════════════════════════════════════════════════════════════════ */

/** §8.5's press. 500 ms is the platform long press everywhere this app runs. */
export const LONG_PRESS_MS = 500;

interface LongPressHandlers {
  readonly onPointerDown: () => void;
  readonly onPointerUp: () => void;
  readonly onPointerCancel: () => void;
  readonly onPointerLeave: () => void;
  readonly onClick: () => void;
}

/**
 * A press that is either a tap or a hold, never both.
 *
 * The timer fires the hold and the click that follows it is swallowed exactly once, so
 * a long press cannot also start playback. A pointer that leaves or is cancelled — a
 * scroll, in practice — is neither a tap nor a hold.
 */
function useLongPress(onTap: () => void, onHold: () => void): LongPressHandlers {
  const timer = useRef<number | null>(null);
  const held = useRef(false);

  const clear = useCallback((): void => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => clear, [clear]);

  const onPointerDown = useCallback((): void => {
    held.current = false;
    clear();
    timer.current = window.setTimeout(() => {
      timer.current = null;
      held.current = true;
      onHold();
    }, LONG_PRESS_MS);
  }, [clear, onHold]);

  const onClick = useCallback((): void => {
    clear();
    if (held.current) {
      held.current = false;
      return;
    }
    onTap();
  }, [clear, onTap]);

  return {
    onPointerDown,
    onPointerUp: clear,
    onPointerCancel: clear,
    onPointerLeave: clear,
    onClick,
  };
}

/* ══ one item ══════════════════════════════════════════════════════════════════════ */

export interface CrateItemProps {
  readonly track: TrackSummary;
  /** `true` when this record is the one loaded in the element. */
  readonly current: boolean;
  readonly filed: boolean;
  readonly onPlay: () => void;
  readonly onFile: () => void;
  readonly onPlayNext: () => void;
  readonly onShare: () => void;
  readonly onRemove: () => void;
}

export function CrateItem({
  track,
  current,
  filed,
  onPlay,
  onFile,
  onPlayNext,
  onShare,
  onRemove,
}: CrateItemProps): JSX.Element {
  const [flipped, setFlipped] = useState(false);
  const stripId = useId();

  const flip = useCallback((): void => {
    setFlipped((was) => !was);
  }, []);

  const press = useLongPress(onPlay, flip);

  /* The wire carries `plate.seed`; the pattern, tilt and ink pair are re-derived from
     it exactly as `LabelChip` does, so one record is the same object at 44px and at
     140px. The catalogue number printed on the sleeve is the row's own, not the
     plate's re-derivation of it. */
  const plate: Plate = useMemo(
    () => plateFromSeed(track.plate.seed, track.id),
    [track.plate.seed, track.id],
  );

  const style = {
    ...plateCssVars(plate),
    '--sleeve-lift': sleeveLift(plate.seed),
  } as CSSProperties;

  const name =
    track.performer === null || track.performer === ''
      ? track.title
      : `${track.title}, ${track.performer}`;

  return (
    <div
      className={styles.item}
      data-track-id={String(track.id)}
      /* The plate's ink table lives in `skins.css`, keyed by this attribute — see
         `plateCssVars`. Without it the sleeve falls back to the unpainted default. */
      data-pair={String(plate.pair)}
      style={style}
    >
      <div className={styles.card} data-flipped={flipped ? 'true' : 'false'}>
        <button
          type="button"
          className={styles.record}
          aria-label={name}
          aria-current={current ? true : undefined}
          data-lifted={current ? 'true' : 'false'}
          onPointerDown={press.onPointerDown}
          onPointerUp={press.onPointerUp}
          onPointerCancel={press.onPointerCancel}
          onPointerLeave={press.onPointerLeave}
          onClick={press.onClick}
        >
          <span className={styles.disc} aria-hidden="true">
            <span className={styles.spindle} />
          </span>
          <span className={styles.sleeve}>
            <span className={cx('t-display', 't-13', 't-clamp-1', styles.sleeveTitle)}>
              {track.title}
            </span>
            <span className={cx('t-mono', 't-11', styles.sleeveCat)}>{track.catalogueNo}</span>
          </span>
        </button>

        <div className={styles.corner}>
          <button
            type="button"
            className={styles.more}
            aria-label={MORE_LABEL}
            aria-expanded={flipped}
            aria-controls={stripId}
            onClick={flip}
          >
            <span className={styles.moreGlyph} aria-hidden="true" />
          </button>
          <FileBracket filed={filed} onToggle={onFile} />
        </div>

        {flipped ? (
          <div className={styles.back} id={stripId} data-strip="true">
            <SpecTable
              rows={[
                { label: 'SIDE', value: track.plate.side },
                { label: 'YEAR', value: track.year },
                { label: 'BPM', value: track.bpm },
                { label: 'CAT №', value: track.catalogueNo },
                { label: 'LENGTH', value: formatTimecode(track.durationS) },
              ]}
            />

            <div className={styles.strip} role="group" aria-label={name}>
              <button
                type="button"
                className={cx('t-mono', 't-11', styles.stripButton)}
                aria-label={CRATE_ACTIONS.next.ru}
                onClick={onPlayNext}
              >
                {CRATE_ACTIONS.next.print}
              </button>
              <button
                type="button"
                className={cx('t-mono', 't-11', styles.stripButton)}
                aria-label={CRATE_ACTIONS.share.ru}
                onClick={onShare}
              >
                {CRATE_ACTIONS.share.print}
              </button>
              <button
                type="button"
                className={cx('t-mono', 't-11', styles.stripButton)}
                aria-label={CRATE_ACTIONS.remove.ru}
                data-danger="true"
                onClick={onRemove}
              >
                {CRATE_ACTIONS.remove.print}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ══ the screen ════════════════════════════════════════════════════════════════════ */

/**
 * Injectable collaborators.
 *
 * The screen defaults to the app-wide singletons and every one of them can be
 * replaced, which is how the suite drives a real `store/library` over a fake
 * `ApiClient` rather than mocking the module graph. `createWiring`'s `WiringDeps` sets
 * the precedent.
 */
export interface CrateDeps {
  readonly client?: ApiClient;
  readonly library?: typeof useLibraryStore;
  readonly queue?: typeof useQueueStore;
  readonly player?: typeof usePlayerStore;
  readonly wiring?: Pick<Wiring, 'select'>;
  readonly telegram?: TelegramFacade;
}

export type MyCrateProps = ScreenProps & { readonly deps?: CrateDeps };

export function MyCrate({ deps = {} }: MyCrateProps): JSX.Element {
  const client = deps.client ?? api;
  const library = deps.library ?? useLibraryStore;
  const queue = deps.queue ?? useQueueStore;
  const player = deps.player ?? usePlayerStore;
  const tg = deps.telegram ?? defaultTelegram;

  const [sort, setSort] = useState<LibrarySort>('recent');
  /** Stable for the life of one `RANDOM` listing, including all of its pages (§6.6). */
  const [seed, setSeed] = useState<number | null>(null);
  const [actionError, setActionError] = useState<ApiFailure | null>(null);

  const page = library((state) => state.favoritesPage);
  const playlists = library((state) => state.playlists);
  const currentTrackId = player((state) => state.trackId);

  /* Subscribing to the favourites map is what repaints a bracket the instant a toggle
     lands (and again if it rolls back). The value is deliberately not read here:
     `selectIsFavorite` below is the store's single sanctioned read, and copying its
     body would create the second source of truth `store/library`'s header exists to
     prevent. */
  library((state) => state.favorites);

  const args = useMemo<FavoritesArgs>(
    () => (sort === 'random' && seed !== null ? { sort, seed } : { sort }),
    [sort, seed],
  );

  useEffect(() => {
    void library.getState().loadFavorites(args);
  }, [library, args]);

  useEffect(() => {
    void library.getState().loadPlaylists();
  }, [library]);

  const chooseSort = useCallback((next: LibrarySort): void => {
    /* Re-tapping RANDOM re-cuts the order, because a new seed is the only thing that
       can change a seeded listing. The first tap does the same. */
    setSeed(next === 'random' ? newSeed() : null);
    setSort(next);
  }, []);

  const items = page.items;

  const play = useCallback(
    async (index: number): Promise<void> => {
      /* "Play all of this, starting here": one `PUT /api/queue` with the ordered id
         list (§6.7), then one play command. The crate the user is looking at becomes
         the set list, which is what a selector means by pulling a record. */
      await queue.getState().replace(items, index);
      const w = deps.wiring ?? getWiring();
      await w.select(index);
    },
    [deps.wiring, items, queue],
  );

  const share = useCallback(
    async (trackId: number): Promise<void> => {
      try {
        const response = await client.shareTrack({
          trackId,
          allow: { user: true, group: true, channel: true, bot: false },
        });
        if (response.mode === 'prepared') {
          const sent = await tg.shareMessage(response.preparedMessageId);
          if (sent) return;
        }
        /* §6.8: a historical track the bot never saw has no cached audio to attach, so
           the share falls through to the link. The same path covers a prepared message
           the client declined to send. */
        tg.openTelegramLink(response.shareUrl);
      } catch (error) {
        setActionError(toApiFailure(error));
      }
    },
    [client, tg],
  );

  const retry = useCallback((): void => {
    void library.getState().loadFavorites(args);
  }, [library, args]);

  const complete = page.status === 'ready' && page.nextCursor === null;
  const meta = crateMetaLine(page.total, items, complete);

  const masthead = (
    <header className={styles.masthead}>
      <Halftone as="div">
        <h1 className={cx('t-display', 't-28', styles.mastheadTitle)}>{MY_CRATE_TITLE}</h1>
      </Halftone>

      {meta === null ? null : (
        <p className={cx('t-mono', 't-13', styles.cyr, styles.mastheadMeta)} data-crate-meta="true">
          {meta}
        </p>
      )}

      <div className={styles.switch} role="group" aria-label={SORT_SWITCH_LABEL_RU}>
        {CRATE_SORTS.map((option) => (
          <TagChip
            key={option.sort}
            label={option.print}
            active={sort === option.sort}
            onToggle={() => {
              chooseSort(option.sort);
            }}
          />
        ))}
      </div>

      <div className={styles.headerActions}>
        <button
          type="button"
          className={styles.historyLink}
          onClick={() => {
            navigate(buildPath('history'));
          }}
        >
          <span className={cx('t-mono', 't-13', styles.cyr)}>{HISTORY_LINK_RU}</span>
        </button>

        <button
          type="button"
          className={styles.historyLink}
          onClick={() => {
            navigate(buildPath('skins'));
          }}
        >
          <span className={cx('t-mono', 't-13', styles.cyr)}>{SKINS_LINK_RU}</span>
        </button>
      </div>

      {actionError === null ? null : (
        <p
          className={cx('t-mono', 't-13', styles.cyr, styles.actionError)}
          role="alert"
          data-action-error="true"
        >
          {`${actionError.displayCode} · ${actionError.message}`}
        </p>
      )}

      {playlists.length === 0 ? null : (
        <>
          <DownbeatRule label={PLAYLISTS_HEADING_RU} />
          <ul className={styles.playlists}>
            {playlists.map((playlist: PlaylistHeader) => (
              <li key={playlist.id} className={styles.playlistRow}>
                <button
                  type="button"
                  className={styles.playlistLink}
                  onClick={() => {
                    navigate(buildPath('playlist', { id: String(playlist.id) }));
                  }}
                >
                  <span className={cx('t-ui', 't-17', 't-clamp-1')}>{playlist.title}</span>
                  <span className={cx('t-mono', 't-11', styles.playlistMeta)}>
                    {`${String(playlist.trackCount)} · ${formatRuntime(playlist.durationS)}`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </header>
  );

  let body: JSX.Element;
  if (page.status === 'error' && page.error !== null && items.length === 0) {
    body = <ErrorState failure={page.error} grid="shelf" onRetry={retry} />;
  } else if (page.status === 'ready' && items.length === 0) {
    body = <Empty variant="favorites" />;
  } else if (items.length === 0) {
    body = <Loading grid="shelf" rows={4} />;
  } else {
    body = (
      <StateFrame grid="shelf" state="content" role="group" ariaLabel={SHELF_LABEL_RU}>
        {items.map((track, index) => (
          <CrateItem
            key={track.id}
            track={track}
            current={currentTrackId === track.id}
            filed={selectIsFavorite(track.id)(library.getState())}
            onPlay={() => {
              void play(index);
            }}
            onFile={() => {
              void library.getState().toggleFavorite(track.id);
            }}
            onPlayNext={() => {
              void queue.getState().enqueueNext([track]);
            }}
            onShare={() => {
              void share(track.id);
            }}
            onRemove={() => {
              void library.getState().toggleFavorite(track.id);
            }}
          />
        ))}
      </StateFrame>
    );
  }

  return (
    <div className={styles.screen} data-screen="favorites">
      {masthead}
      {body}
    </div>
  );
}
